/**
 * 横断検索（パソコンは ⌘K / Ctrl+K、スマホは上部の検索アイコン）。
 * 画面名・申請番号・台帳・台帳の行・評価基準番号（例「4-1-3-4」）を1か所から探す。
 *
 * 候補は開いたときに読む（画面・台帳・評価基準・申請）。台帳の行は、この端末で開いた台帳の行を
 * 覚えておいて探す（全台帳の全行を毎回取りに行かない）。
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "../api.js";
import { useI18n } from "../useI18n.js";
import { can, onUserCleared, useMe } from "../useMe.js";
import { NAV, navItemVisible } from "../routes.js";
import { Sheet } from "../ui/Sheet.js";
import type { RegisterSummary, RequestSummary } from "@scs/shared";

interface Hit { kind: string; label: string; sub?: string; href: string; hay: string }

// 台帳の行（開いた台帳の分だけ）。RegisterEntries が読み込んだときに足す。
const rowIndex = new Map<string, Hit[]>();
export function indexRegisterRows(code: string, registerName: string, rows: Array<{ naturalKey: string; data: Record<string, unknown> }>): void {
  rowIndex.set(code, rows.slice(0, 2000).map((r) => {
    const sample = Object.values(r.data).filter((v) => typeof v === "string").slice(0, 3).join(" ");
    return { kind: "row", label: r.naturalKey, sub: `${registerName} ${sample}`.slice(0, 80), href: `#/registers/${code}?key=${encodeURIComponent(r.naturalKey)}`, hay: `${r.naturalKey} ${sample}`.toLowerCase() };
  }));
}

let cached: { registers: Hit[]; criteria: Hit[]; requests: Hit[] } | null = null;
// 読み込み中に利用者が替わったら、その結果は捨てる（前の人の候補を次の人に入れない）
let generation = 0;

/** M2: ログアウト・セッション切れで、覚えている台帳の行と候補を全部消す。 */
export function resetSearchIndex(): void {
  rowIndex.clear();
  cached = null;
  generation++;
}
onUserCleared(resetSearchIndex);

export function SearchPalette({ open, onClose, go }: { open: boolean; onClose: () => void; go: (href: string) => void }) {
  const { t } = useI18n();
  const me = useMe();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [data, setData] = useState(cached);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setActive(0);
    const gen = generation;
    const scope = can(me, "requests:read-all") ? ["all"] : ["mine", "pending"];
    Promise.all([
      // 読めない台帳は候補に出さない（選ぶと 403 になるだけ。M1）
      api.get<{ registers: RegisterSummary[] }>("/registers").then((r) => r.registers.filter((x) => x.canRead !== false).map<Hit>((x) => ({
        kind: "register", label: x.name, sub: x.formNo ?? undefined, href: `#/registers/${x.code}`, hay: `${x.name} ${x.formNo ?? ""} ${x.code}`.toLowerCase(),
      }))).catch(() => [] as Hit[]),
      api.get<{ criteria: Array<{ code: string; requirementName: string; deliverable: string | null }> }>("/criteria?level=STAR3").then((r) => r.criteria.map<Hit>((c) => ({
        kind: "criterion", label: c.code, sub: c.requirementName, href: `#/criteria?code=${c.code}`, hay: `${c.code} ${c.requirementName} ${c.deliverable ?? ""}`.toLowerCase(),
      }))).catch(() => [] as Hit[]),
      Promise.all(scope.map((s) => api.get<{ requests: RequestSummary[] }>(`/requests?scope=${s}`).then((r) => r.requests).catch(() => [] as RequestSummary[])))
        .then((lists) => {
          const seen = new Set<string>();
          return lists.flat().filter((r) => !seen.has(r.id) && seen.add(r.id)).map<Hit>((r) => ({
            kind: "request", label: r.requestNo, sub: r.typeName, href: `#/requests/${r.id}`, hay: `${r.requestNo} ${r.typeName}`.toLowerCase(),
          }));
        }),
    ]).then(([registers, criteria, requests]) => {
      if (gen !== generation) return;
      cached = { registers, criteria, requests }; setData(cached);
    });
  }, [open, me]);

  const pages = useMemo<Hit[]>(() => {
    const out: Hit[] = [];
    // L-11: 社員は「やりたいこと」の言葉で探す。画面ごとに日本語・英語の別名を持たせる
    const alias: Record<string, string> = {
      "#/requests": "申請 承認 差し戻し request approval",
      "#/incidents": "インシデント 事故 報告 漏えい 紛失 incident",
      "#/training": "教育 研修 受講 テスト 確認テスト 教材 training quiz course",
      "#/notifications": "通知 お知らせ notice",
      "#/obligations": "期限 点検 報告 締切 deadline inspection",
      "#/roles": "体制 担当 連絡先 責任者 roles contact",
    };
    for (const g of NAV) for (const it of g.items) if (navItemVisible(it, me)) out.push({ kind: "page", label: t(it.label), href: it.href, hay: `${t(it.label)} ${alias[it.href] ?? ""}`.toLowerCase() });
    out.push({ kind: "page", label: t("inc.report"), href: "#/incidents/new", hay: `${t("inc.report")} 報告 第一報 事故 紛失 盗難 ウイルス report incident`.toLowerCase() });
    out.push({ kind: "page", label: t("req.new"), href: "#/requests/new", hay: `${t("req.new")} 申請 ソフト ソフトウェア ツール アプリ アカウント ID 権限 アクセス 本番 new request software account access`.toLowerCase() });
    out.push({ kind: "page", label: t("nav.settings"), href: "#/settings", hay: `${t("nav.settings")} 設定 パスワード 認証アプリ スマホ 機種変更 言語 settings totp password language`.toLowerCase() });
    out.push({ kind: "page", label: t("cal.title"), href: "#/settings?focus=calendar", hay: `${t("cal.title")} カレンダー 予定 google outlook calendar`.toLowerCase() });
    return out;
  }, [t, me]);

  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = [...pages, ...(data?.requests ?? []), ...(data?.criteria ?? []), ...(data?.registers ?? []), ...[...rowIndex.values()].flat()];
    if (!s) return pages.slice(0, 12);
    const terms = s.split(/\s+/);
    return all.filter((h) => terms.every((x) => h.hay.includes(x))).slice(0, 30);
  }, [q, pages, data]);

  function choose(h: Hit | undefined) {
    if (!h) return;
    onClose();
    go(h.href);
  }

  return (
    <Sheet open={open} onClose={onClose} title={t("search.title")} variant="dialog" className="palette" initialFocus="input">
      <div className="palette-input">
        <label className="visually-hidden" htmlFor={`${listId}-q`}>{t("search.label")}</label>
        <input id={`${listId}-q`} ref={inputRef} type="search" role="combobox" autoComplete="off" enterKeyHint="go"
               aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
               aria-activedescendant={hits[active] ? `${listId}-${active}` : undefined}
               placeholder={t("search.placeholder")} value={q}
               onChange={(e) => { setQ(e.target.value); setActive(0); }}
               onKeyDown={(e) => {
                 if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
                 else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                 else if (e.key === "Enter") { e.preventDefault(); choose(hits[active]); }
               }} />
        <p className="field-hint">{t("search.hint")}</p>
      </div>
      <ul className="palette-list" role="listbox" id={listId} aria-label={t("search.results")}>
        {hits.map((h, i) => (
          <li key={`${h.kind}:${h.href}`} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              onMouseMove={() => setActive(i)} onClick={() => choose(h)}>
            <span className="p-kind">{t(`search.kind.${h.kind}`)}</span>
            <span className="p-label">{h.label}{h.sub && <span className="muted small"> {h.sub}</span>}</span>
          </li>
        ))}
        {hits.length === 0 && <li className="muted" style={{ padding: "var(--s3) var(--s4)" }}>{t("search.none")}</li>}
      </ul>
    </Sheet>
  );
}
